import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '@/context/AuthContext';
import Navbar from '@/components/Navbar';
import { TrustBar } from '@/components/TrustBadges';
import { Eye, EyeSlash } from '@phosphor-icons/react';

export default function AuthPage() {
  const [searchParams] = useSearchParams();
  const initialMode = searchParams.get('mode') || 'login';
  const initialRole = searchParams.get('role') || 'customer';
  const [mode, setMode] = useState(initialMode);
  const [role, setRole] = useState(initialRole);
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPass, setShowPass] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [googleClientId, setGoogleClientId] = useState(null); // null = checking, '' = not configured
  const googleBtnRef = useRef(null);
  const roleRef = useRef(role); // latest Book/Offer choice for the Google callback (avoids stale closure)
  const submittingRef = useRef(false); // an in-progress auth action owns its own redirect
  const navigate = useNavigate();
  const { user, login, register, setToken, setUser, API } = useAuth();

  // Already logged in? Don't show a register/login form — route them where they belong.
  // A logged-in customer who clicks "Apply as a provider" lands on onboarding (which
  // upgrades their existing account) instead of a confusing "create account" screen.
  useEffect(() => {
    if (!user) return;
    // A login/register/Google action just fired — let its handler pick the
    // destination (e.g. a fresh provider goes to the setup form, not the dashboard).
    if (submittingRef.current) return;
    if (user.role === 'admin') navigate('/admin', { replace: true });
    else if (user.role === 'provider') navigate('/provider/dashboard', { replace: true });
    else if (searchParams.get('role') === 'provider') navigate('/provider/setup-profile', { replace: true });
    else navigate('/dashboard', { replace: true });
  }, [user, navigate, searchParams]);

  // Keep a ref to the current role so the Google (GIS) callback — set up once —
  // always sees the latest Book/Offer choice instead of a stale value.
  useEffect(() => { roleRef.current = role; }, [role]);

  // Our own Google sign-in (Google Identity Services) when GOOGLE_CLIENT_ID is
  // configured; otherwise the legacy Emergent-hosted flow below stays in place.
  useEffect(() => {
    axios.get(`${API}/auth/google/config`)
      .then(r => setGoogleClientId(r.data.client_id || ''))
      .catch(() => setGoogleClientId(''));
  }, [API]);

  useEffect(() => {
    if (!googleClientId) return;
    const init = () => {
      if (!googleBtnRef.current) return;
      window.google.accounts.id.initialize({
        client_id: googleClientId,
        callback: async (resp) => {
          try {
            submittingRef.current = true;
            const r = await axios.post(`${API}/auth/google`, { credential: resp.credential }, { withCredentials: true });
            sessionStorage.setItem('bookr_token', r.data.token);
            setToken(r.data.token);
            setUser(r.data.user);
            const u = r.data.user;
            const hasPendingPlan = !!sessionStorage.getItem('bookr_pending_plan');
            if (u.role === 'admin') navigate('/admin');
            else if (u.role === 'provider') navigate('/provider/dashboard');
            // Chose "Offer Services" (or arrived via "Apply as a provider")? Go straight
            // to the setup form — a fresh Google account is a customer until they submit.
            else if (roleRef.current === 'provider' || searchParams.get('role') === 'provider') navigate('/provider/setup-profile');
            else navigate(hasPendingPlan ? '/ai-planner' : '/dashboard');
          } catch (err) {
            setError(err?.response?.data?.detail || 'Google sign-in failed. Please try again.');
          }
        },
      });
      window.google.accounts.id.renderButton(googleBtnRef.current, { theme: 'outline', size: 'large', width: 380, text: 'continue_with', shape: 'pill' });
    };
    if (window.google?.accounts?.id) { init(); return; }
    const s = document.createElement('script');
    s.src = 'https://accounts.google.com/gsi/client';
    s.async = true;
    s.onload = init;
    document.head.appendChild(s);
    // searchParams is read inside the Google callback but intentionally omitted
    // from deps — we don't want to re-init the Google button on every URL change.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [googleClientId, API, navigate, setToken, setUser]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    submittingRef.current = true;
    setLoading(true);
    try {
      // If the visitor started a plan in the landing page chat, continue it after auth
      const hasPendingPlan = !!sessionStorage.getItem('bookr_pending_plan');
      if (mode === 'login') {
        const userData = await login(email, password);
        if (userData.role === 'admin') navigate('/admin');
        else if (userData.role === 'provider') navigate('/provider/dashboard');
        else navigate(hasPendingPlan ? '/ai-planner' : '/dashboard');
      } else {
        const userData = await register(name, email, password, role);
        if (role === 'provider') navigate('/provider/setup-profile');
        else navigate(hasPendingPlan ? '/ai-planner' : '/dashboard');
      }
    } catch (err) {
      submittingRef.current = false; // attempt failed — let the effect resume its job
      setError(err.response?.data?.detail || 'Something went wrong');
    } finally {
      setLoading(false);
    }
  };

  const inputCls = "w-full h-11 rounded-xl border border-border bg-card px-4 text-sm text-foreground focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/40 focus:ring-2 focus:ring-primary/40";

  return (
    <div className="min-h-screen bg-hero-wash" data-testid="auth-page">
      <Navbar />
      <div className="max-w-md mx-auto px-4 sm:px-6 py-16 md:py-20">
        <div className="bg-card rounded-2xl border border-border/70 shadow-float p-6 sm:p-8">
          <h1 className="text-3xl sm:text-4xl font-display font-semibold tracking-tight text-foreground mb-2">
            {mode === 'login' ? 'Welcome back' : 'Create an account'}
          </h1>
          <p className="text-muted-foreground leading-relaxed mb-8">
            {mode === 'login' ? 'So glad to see you again — sign in to your Bookr.ai account.' : 'Join the marketplace for local services. It only takes a minute.'}
          </p>

          {error && (
            <div className="bg-accent-coral/10 text-accent-coral rounded-xl px-4 py-3 text-sm font-medium mb-6" data-testid="auth-error">
              {error}
            </div>
          )}

          {/* Pick your path first — this governs both Google and email sign-up below */}
          {mode === 'register' && (
            <div className="mb-6" data-testid="role-selector">
              <label className="block text-sm font-medium text-foreground mb-1.5">I want to</label>
              <div className="grid grid-cols-2 gap-2">
                <button
                  type="button"
                  onClick={() => setRole('customer')}
                  className={`rounded-xl border px-3 py-2.5 text-left btn-anim cursor-pointer ${role === 'customer' ? 'bg-primary text-primary-foreground border-primary shadow-soft' : 'border-border bg-card text-foreground hover:bg-secondary/60'}`}
                  data-testid="role-customer-button"
                >
                  <span className="block text-sm font-semibold">Book Services</span>
                  <span className={`block text-[11px] font-normal ${role === 'customer' ? 'text-primary-foreground/80' : 'text-muted-foreground'}`}>Plan &amp; hire for my event</span>
                </button>
                <button
                  type="button"
                  onClick={() => setRole('provider')}
                  className={`rounded-xl border px-3 py-2.5 text-left btn-anim cursor-pointer ${role === 'provider' ? 'bg-primary text-primary-foreground border-primary shadow-soft' : 'border-border bg-card text-foreground hover:bg-secondary/60'}`}
                  data-testid="role-provider-button"
                >
                  <span className="block text-sm font-semibold">Offer Services</span>
                  <span className={`block text-[11px] font-normal ${role === 'provider' ? 'text-primary-foreground/80' : 'text-muted-foreground'}`}>Get booked as a provider</span>
                </button>
              </div>
            </div>
          )}

          {/* Our own Google Sign-In (Google Identity Services) — only rendered
              when GOOGLE_CLIENT_ID is configured. No third-party fallback:
              email/password below always works, so we never show a Google
              button that can't complete. */}
          {googleClientId && (
            <>
              <div ref={googleBtnRef} className="mb-6 flex justify-center" data-testid="google-gsi-button" />
              <div className="flex items-center gap-4 mb-6">
                <div className="flex-1 h-px bg-border" />
                <span className="text-xs text-muted-foreground uppercase tracking-widest">or</span>
                <div className="flex-1 h-px bg-border" />
              </div>
            </>
          )}

          <form onSubmit={handleSubmit} className="space-y-4">
            {mode === 'register' && (
              <div>
                <label className="block text-sm font-medium text-foreground mb-1.5">Full name</label>
                <input
                  type="text"
                  value={name}
                  onChange={e => setName(e.target.value)}
                  className={inputCls}
                  required
                  data-testid="register-name-input"
                />
              </div>
            )}
            <div>
              <label className="block text-sm font-medium text-foreground mb-1.5">Email</label>
              <input
                type="email"
                value={email}
                onChange={e => setEmail(e.target.value)}
                className={inputCls}
                required
                data-testid="auth-email-input"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-foreground mb-1.5">Password</label>
              <div className="relative">
                <input
                  type={showPass ? 'text' : 'password'}
                  value={password}
                  onChange={e => setPassword(e.target.value)}
                  className={`${inputCls} pr-11`}
                  required
                  minLength={6}
                  data-testid="auth-password-input"
                />
                <button type="button" onClick={() => setShowPass(!showPass)} aria-label={showPass ? 'Hide password' : 'Show password'} className="absolute right-1 top-1/2 -translate-y-1/2 w-11 h-11 flex items-center justify-center text-muted-foreground hover:text-foreground transition-colors cursor-pointer">
                  {showPass ? <EyeSlash size={18} /> : <Eye size={18} />}
                </button>
              </div>
              {mode === 'login' && (
                <div className="text-right mt-1.5">
                  <Link to="/forgot-password" className="text-xs text-primary font-medium hover:underline" data-testid="forgot-password-link">Forgot password?</Link>
                </div>
              )}
            </div>
            <button
              type="submit"
              disabled={loading}
              className="w-full bg-primary text-primary-foreground rounded-xl px-6 h-11 text-sm font-semibold btn-anim shadow-soft hover:shadow-lift hover:bg-primary/90 disabled:opacity-50 cursor-pointer"
              data-testid="auth-submit-button"
            >
              {loading ? 'Please wait...' : mode === 'login' ? 'Sign In' : 'Create Account'}
            </button>
          </form>

          <p className="text-sm text-muted-foreground text-center mt-6">
            {mode === 'login' ? "Don't have an account? " : 'Already have an account? '}
            <button
              onClick={() => setMode(mode === 'login' ? 'register' : 'login')}
              className="text-primary font-medium hover:underline transition-colors cursor-pointer min-h-[44px]"
              data-testid="auth-toggle-mode"
            >
              {mode === 'login' ? 'Sign Up' : 'Sign In'}
            </button>
          </p>
        </div>
        <TrustBar className="mt-6" />
      </div>
    </div>
  );
}
